import * as React from "react";
import {
    Dialog,
    DialogContent,
    Button,
    TextArea,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateConsent } from "../../hooks/mutations/reports.mutations";
import { IConsentReport } from "../../types/reports.types";
import useToast from "../../../../hooks/useToast";
import { formatDate } from "../../../../utils/helpers";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";

interface ActivityLogModalProps {
    isOpen: boolean;
    consent: IConsentReport | null;
    onClose: () => void;
}

// Helper function to parse description text and format updates as list items
const parseDescriptionUpdates = (description: string): string[] => {
    // Split by comma and "and" to separate multiple updates
    const updates = description
        .split(/,\s*(?![^(]*\))|(?:\s+and\s+)/)
        .map((update) => update.trim())
        .filter((update) => update.length > 0);

    // Format dates in each update
    return updates.map((update) => {
        // Find dates in YYYY-MM-DD format and convert them
        return update.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (match) => {
            return formatDate(match);
        });
    });
};

export default function ActivityLogModal({
    isOpen,
    consent,
    onClose,
}: ActivityLogModalProps) {
    const [activityNote, setActivityNote] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [currentConsent, setCurrentConsent] =
        React.useState<IConsentReport | null>(consent);

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const updateConsent = useUpdateConsent();

    // Update local state when consent prop changes
    React.useEffect(() => {
        setCurrentConsent(consent);
    }, [consent]);

    const handleSubmitNote = async () => {
        if (!currentConsent || !activityNote.trim()) return;

        setIsSubmitting(true);
        try {
            await updateConsent.mutateAsync({
                consentId: currentConsent.consent_id,
                data: {
                    activity_note: activityNote.trim(),
                },
            });

            // Refetch all consent-reports queries
            await queryClient.refetchQueries({ queryKey: ["consent-reports"] });

            // Get all matching queries and find the updated consent
            const queries = queryClient.getQueriesData<
                IPaginatedApiResponse<IConsentReport[]>
            >({
                queryKey: ["consent-reports"],
            });

            // Find the updated consent from any matching query
            const updatedConsent = queries
                .map(([, data]) => data?.data)
                .filter(Boolean)
                .flat()
                .find((c) => c?.consent_id === currentConsent.consent_id);

            if (updatedConsent) {
                setCurrentConsent(updatedConsent);
            }

            toast({
                mode: "success",
                message: "Activity note added successfully",
            });
            setActivityNote("");
        } catch (error) {
            toast({
                mode: "error",
                message: "Could not add activity note at this time",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setActivityNote("");
        onClose();
    };

    if (!isOpen || !currentConsent) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent
                variant="drawer"
                title="Activity History"
                handleCloseDialog={handleClose}
                showFooter={false}
            >
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto mb-6">
                        {currentConsent.consent_cc_notes ? (
                            <div className="flex flex-col gap-1 bg-[--card-bg-gray] border border-gray-200 p-8 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-gray-900">
                                    Consent CC Notes:
                                </p>
                                <p className="text-sm text-gray-500">
                                    {currentConsent.consent_cc_notes}
                                </p>
                            </div>
                        ) : null}
                        {currentConsent.activity_log &&
                        currentConsent.activity_log.length > 0 ? (
                            <div className="space-y-4">
                                {currentConsent.activity_log
                                    .slice()
                                    .reverse()
                                    .map((activity) => (
                                        <div key={activity.activity_id}>
                                            {/* Activity content */}
                                            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {activity.user_name}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(
                                                            activity.timestamp
                                                        ).toLocaleString(
                                                            "en-US",
                                                            {
                                                                month: "short",
                                                                day: "numeric",
                                                                year: "numeric",
                                                                hour: "numeric",
                                                                minute: "2-digit",
                                                                hour12: true,
                                                            }
                                                        )}
                                                    </span>
                                                </div>

                                                {/* Parse and display description updates as list */}
                                                {(() => {
                                                    const updates =
                                                        parseDescriptionUpdates(
                                                            activity.description
                                                        );
                                                    if (updates.length > 1) {
                                                        return (
                                                            <div className="space-y-1">
                                                                {updates.map(
                                                                    (
                                                                        update
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                update
                                                                            }
                                                                            className="text-sm text-gray-700 flex items-start"
                                                                        >
                                                                            <span className="mr-2">
                                                                                •
                                                                            </span>
                                                                            <span>
                                                                                {
                                                                                    update
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <p className="text-sm text-gray-700">
                                                            {updates[0] ||
                                                                activity.description}
                                                        </p>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                                    <svg
                                        className="w-8 h-8 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                </div>
                                <p className="text-sm text-gray-500">
                                    No activity history available
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="border-t border-gray-200 pt-4 bg-white">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            Add Activity Note
                        </h3>
                        <TextArea
                            value={activityNote}
                            onChange={(e) => setActivityNote(e.target.value)}
                            placeholder="Add a note about this consent..."
                            rows={3}
                            className="w-full p-3 border mb-3"
                        />
                        <div className="flex gap-2">
                            <Button
                                onClick={handleSubmitNote}
                                disabled={!activityNote.trim() || isSubmitting}
                                variant="primary"
                            >
                                {isSubmitting ? "Adding..." : "Add Note"}
                            </Button>
                            <Button onClick={handleClose} variant="secondary">
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
